import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, DASHBOARD_HOME } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Login, { ResetPassword } from "@/pages/Login";
import Storefront from "@/pages/Storefront";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import ThemePreview from "@/pages/ThemePreview";
import VisualEditor from "@/pages/VisualEditor";
import PartnerBranding from "@/pages/partner/PartnerBranding";
import MockPay from "@/pages/MockPay";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminWebsites from "@/pages/admin/AdminWebsites";
import AdminThemes from "@/pages/admin/AdminThemes";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAudit from "@/pages/admin/AdminAudit";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PartnerWebsites from "@/pages/partner/PartnerWebsites";
import WebsiteWizard from "@/pages/partner/WebsiteWizard";
import WebsiteDetail from "@/pages/partner/WebsiteDetail";
import PartnerBilling from "@/pages/partner/PartnerBilling";
import StoreDashboard from "@/pages/store/StoreDashboard";
import StoreProducts from "@/pages/store/StoreProducts";
import StoreOrders from "@/pages/store/StoreOrders";
import StoreCustomers from "@/pages/store/StoreCustomers";
import StoreCoupons from "@/pages/store/StoreCoupons";
import StoreSettings from "@/pages/store/StoreSettings";
import StoreAccount from "@/pages/store/StoreAccount";
import StoreApp from "@/pages/store/StoreApp";
import StoreCategories from "@/pages/store/StoreCategories";
import StoreDomain from "@/pages/store/StoreDomain";
import PwaRuntime from "@/components/PwaRuntime";
import RootDashboard from "@/pages/root/RootDashboard";
import RootAdmins from "@/pages/root/RootAdmins";
import RootAccess from "@/pages/root/RootAccess";
import RootRevenue from "@/pages/root/RootRevenue";
import RootLegal from "@/pages/root/RootLegal";
import LegalPage from "@/pages/LegalPage";
import CustomerAccount from "@/pages/CustomerAccount";
import ServiceDetails from "@/pages/ServiceDetails";
import RootPlans from "@/pages/root/RootPlans";
import RootDomains from "@/pages/root/RootDomains";
import RootImport from "@/pages/root/RootImport";
import RootSeo from "@/pages/root/RootSeo";
import StoreSeo from "@/pages/store/StoreSeo";
import StoreCrm from "@/pages/store/StoreCrm";
import FormPublic from "@/components/crm/FormPublic";
import ReferralLanding, { StartRedirect } from "@/pages/ReferralLanding";
import SelfServiceSetup from "@/pages/SelfServiceSetup";
import PartnerReferrals from "@/pages/partner/PartnerReferrals";
import RootSelfService from "@/pages/root/RootSelfService";
import RootCrm from "@/pages/root/RootCrm";
import RootAi from "@/pages/root/RootAi";
import RootChallenges from "@/pages/root/RootChallenges";
import AiStudio from "@/pages/store/AiStudio";
import GrowthAi from "@/pages/store/GrowthAi";
import PartnerLeaderboard from "@/pages/partner/PartnerLeaderboard";
import PartnerChallenges from "@/pages/partner/PartnerChallenges";
import MarketplaceHome from "@/pages/marketplace/MarketplaceHome";
import MarketplaceSearch from "@/pages/marketplace/MarketplaceSearch";
import MarketplaceItem from "@/pages/marketplace/MarketplaceItem";
import FreelancerProfile from "@/pages/marketplace/FreelancerProfile";
import MyOrders from "@/pages/marketplace/MyOrders";
import MyCourses from "@/pages/marketplace/MyCourses";
import Messages from "@/pages/marketplace/Messages";
import Offers from "@/pages/marketplace/Offers";
import FreelancerDashboard from "@/pages/freelancer/FreelancerDashboard";
import FreelancerListings from "@/pages/freelancer/FreelancerListings";
import FreelancerOrders from "@/pages/freelancer/FreelancerOrders";
import FreelancerWallet from "@/pages/freelancer/FreelancerWallet";
import RootMarketplace from "@/pages/root/RootMarketplace";
import MarketplaceAuth from "@/pages/marketplace/MarketplaceAuth";
import FreelancerLanding from "@/pages/marketplace/FreelancerLanding";
import FreelancerApply from "@/pages/freelancer/FreelancerApply";
import MyAccount from "@/pages/marketplace/MyAccount";
import VideosHome from "@/pages/videos/VideosHome";
import VideoWatch from "@/pages/videos/VideoWatch";
import CreatorDashboard from "@/pages/videos/CreatorDashboard";
import CreatorVideos from "@/pages/videos/CreatorVideos";
import CreatorOffers from "@/pages/videos/CreatorOffers";
import CreatorEarnings from "@/pages/videos/CreatorEarnings";
import { CreatorLogin, CreatorJoin } from "@/pages/videos/CreatorAuth";
import CreatorLinks from "@/pages/videos/CreatorLinks";
import CreatorAnalytics from "@/pages/videos/CreatorAnalytics";
import CreatorProfile from "@/pages/videos/CreatorProfile";
import CreatorSettings from "@/pages/videos/CreatorSettings";
import CreatorSupport from "@/pages/videos/CreatorSupport";
import CreatorMonetization from "@/pages/videos/CreatorMonetization";
import CreatorReferrals from "@/pages/videos/CreatorReferrals";
import CreatorSiteAffiliate from "@/pages/videos/CreatorSiteAffiliate";
import SiteAffiliateLanding, { SiteAffiliateLegacyRedirect } from "@/pages/SiteAffiliateLanding";
import RootSiteAffiliate from "@/pages/root/RootSiteAffiliate";
import RootAppBranding from "@/pages/root/RootAppBranding";
import RootAppAnalytics from "@/pages/root/RootAppAnalytics";
import ChannelPage from "@/pages/videos/ChannelPage";
import CreatorVerify from "@/pages/videos/CreatorVerify";
import InstallAppBanner from "@/components/InstallAppBanner";
import ShortsPage from "@/pages/videos/ShortsPage";
import FollowingPage from "@/pages/videos/FollowingPage";
import LibraryPage from "@/pages/videos/LibraryPage";
import RESite from "@/pages/realestate/RESite";
import ReDashboard from "@/pages/realestate/ReDashboard";
import ReOrgPanel from "@/pages/realestate/org/ReOrgPanel";
import ReEditor from "@/pages/realestate/org/ReEditor";
import RootRealEstate from "@/pages/root/RootRealEstate";
import OwnerAffiliates from "@/pages/videos/OwnerAffiliates";
import RootCreators from "@/pages/root/RootCreators";
import RootAds from "@/pages/root/RootAds";
import { Zap } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { isCustomHost } from "@/lib/storeUrl";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center animate-pulse">
      <Zap className="h-6 w-6 text-primary-foreground" />
    </div>
  </div>
);

function PwaByRoute() {
  const { pathname } = useLocation();
  const m = pathname.match(/^\/site\/([^/]+)/);
  const custom = isCustomHost();
  const [hostSlug, setHostSlug] = useState(null);
  useEffect(() => {
    if (custom) api.get("/public/resolve-host", { headers: { "X-Miltroo-Host": window.location.hostname } }).then((r) => setHostSlug(r.data.slug)).catch(() => {});
  }, [custom]);
  const slug = m?.[1] || (custom ? hostSlug : null);
  const [store, setStore] = useState(null);
  useEffect(() => {
    if (!slug) { setStore(null); return; }
    try {
      const raw = sessionStorage.getItem(`pwa_store_${slug}`);
      if (raw) { const c = JSON.parse(raw); if (Date.now() - c.ts < 300000) { setStore(c.store); return; } }
    } catch { /* noop */ }
    api.get(`/public/store/${slug}`).then((r) => {
      setStore(r.data.store);
      try { sessionStorage.setItem(`pwa_store_${slug}`, JSON.stringify({ ts: Date.now(), store: r.data.store })); } catch { /* noop */ }
    }).catch(() => setStore(null));
  }, [slug]);
  if (!slug || !store) return null;
  return <PwaRuntime slug={slug} store={store} pathname={pathname} />;
}

const LOGIN_HOME = { CREATOR: "/creator/login", CUSTOMER: "/mkt/auth?mode=login", ROOT_SUPER_ADMIN: "/root-admin/login" };

function RequireRole({ roles }) {
  const { user } = useAuth();
  if (user === undefined) return <Loader />;
  if (!user) return <Navigate to={LOGIN_HOME[roles[0]] || "/login"} replace />;
  if (!roles.includes(user.role)) return <Navigate to={DASHBOARD_HOME[user.role] || "/login"} replace />;
  return <Layout />;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (user === undefined) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={DASHBOARD_HOME[user.role] || "/login"} replace />;
}

function CustomDomainRoutes({ slug }) {
  return (
    <Routes>
      <Route path="/" element={<Storefront slugOverride={slug} />} />
      <Route path="/shop" element={<Storefront slugOverride={slug} shopMode />} />
      <Route path="/products" element={<Storefront slugOverride={slug} shopMode />} />
      <Route path="/product/:pid" element={<ProductPage slugOverride={slug} />} />
      <Route path="/cart" element={<CartPage slugOverride={slug} />} />
      <Route path="/checkout" element={<CheckoutPage slugOverride={slug} />} />
      <Route path="/account" element={<CustomerAccount slugOverride={slug} />} />
      <Route path="/service/:sid" element={<ServiceDetails slugOverride={slug} />} />
      <Route path="/re/*" element={<RESite slugOverride={slug} />} />
      <Route path="*" element={<Storefront slugOverride={slug} />} />
    </Routes>
  );
}

function InvalidDomain({ host }) {
  return (
    <div data-testid="invalid-domain-page" className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md w-full text-center space-y-5 rounded-2xl border border-border bg-card p-10 shadow-sm">
        <div className="mx-auto h-12 w-12 rounded-xl bg-muted flex items-center justify-center text-lg font-bold text-muted-foreground">?</div>
        <h1 className="text-xl font-semibold text-foreground">This domain isn&apos;t connected</h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          <span className="font-medium text-foreground">{host}</span> is not linked to any MILTROO store.
          If you own this domain, connect it from your MILTROO dashboard (Store &rarr; Domain).
        </p>
        <button data-testid="invalid-domain-retry-btn" onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity">
          Retry
        </button>
      </div>
    </div>
  );
}


function PlatformOrSite() {
  const host = window.location.hostname;
  let platformHost = host;
  try { platformHost = new URL(process.env.REACT_APP_BACKEND_URL).hostname; } catch { /* same origin */ }
  const isPlatform = host === platformHost || host === "localhost" || host === "127.0.0.1";
  const [slug, setSlug] = useState(isPlatform ? false : undefined);
  useEffect(() => {
    if (isPlatform) return;
    api.get("/public/resolve-host", { headers: { "X-Miltroo-Host": window.location.hostname } }).then((r) => {
      // Canonical enforcement for EVERY custom domain: force HTTPS and redirect the
      // non-canonical hostname (apex↔www) to the verified working canonical URL.
      const canon = r.data.canonical_url || "";
      if (canon) {
        try {
          const t = new URL(canon);
          const cur = window.location.hostname.toLowerCase().replace(/^www\./, "");
          const tgt = t.hostname.toLowerCase().replace(/^www\./, "");
          // Guard against redirect loops: only follow a canonical that is the apex/www
          // counterpart of THIS domain (never a foreign host).
          if (cur === tgt && (window.location.protocol === "http:" || window.location.hostname.toLowerCase() !== t.hostname.toLowerCase())) {
            window.location.replace(t.origin + window.location.pathname + window.location.search + window.location.hash);
            return;
          }
        } catch { /* invalid canonical — serve as-is */ }
      } else if (window.location.protocol === "http:") {
        window.location.replace(`https://${window.location.host}${window.location.pathname}${window.location.search}${window.location.hash}`);
        return;
      }
      setSlug(r.data.slug);
    }).catch(() => setSlug(false));
  }, [isPlatform]);
  if (slug === undefined) return <Loader />;
  // Custom hosts NEVER fall back to the MILTROO main site: failed/unknown mapping → safe 404 page.
  if (!isPlatform && !slug) return <InvalidDomain host={host} />;
  return slug ? <CustomDomainRoutes slug={slug} /> : <Home />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PlatformOrSite />} />
            <Route path="/dashboard" element={<RoleRedirect />} />
            <Route path="/login" element={<Login role="PARTNER" />} />
            <Route path="/forgot-password" element={<Login role="PARTNER" initialMode="forgot" />} />
            <Route path="/channel/:slug" element={<ChannelPage />} />
            <Route path="/business-owner/login" element={<Login role="BUSINESS_OWNER" />} />
            <Route path="/super-admin/login" element={<Login role="SUPER_ADMIN" />} />
            <Route path="/root-admin/login" element={<Login role="ROOT_SUPER_ADMIN" />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/site/:slug/account" element={<CustomerAccount />} />
            <Route path="/site/:slug/service/:sid" element={<ServiceDetails />} />
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route path="/forms/:publicId" element={<FormPublic />} />
            <Route path="/site/:slug/*" element={<Storefront />} />
            <Route path="/site/:slug/re/*" element={<RESite />} />
            <Route path="/site/:slug/shop" element={<Storefront shopMode />} />
            <Route path="/site/:slug/product/:pid" element={<ProductPage />} />
            <Route path="/site/:slug/cart" element={<CartPage />} />
            <Route path="/site/:slug/checkout" element={<CheckoutPage />} />
            <Route path="/preview/:tid" element={<ThemePreview />} />
            <Route path="/pay/:txnId" element={<MockPay />} />
            <Route path="/start" element={<StartRedirect />} />
            <Route path="/start/:code" element={<ReferralLanding />} />
            <Route path="/self-service/setup" element={<SelfServiceSetup />} />
            <Route path="/marketplace" element={<MarketplaceHome />} />
            <Route path="/marketplace/search" element={<MarketplaceSearch />} />
            <Route path="/marketplace/f/:slug" element={<FreelancerProfile />} />
            <Route path="/marketplace/:kind/:id" element={<MarketplaceItem />} />
            <Route path="/mkt/auth" element={<MarketplaceAuth />} />
            <Route path="/join-freelancer" element={<FreelancerLanding />} />
            <Route path="/freelancer/apply" element={<FreelancerApply />} />
            <Route path="/videos" element={<VideosHome />} />
            <Route path="/videos/shorts" element={<ShortsPage />} />
            <Route path="/videos/following" element={<FollowingPage />} />
            <Route path="/videos/library" element={<LibraryPage />} />
            <Route path="/v/:slug" element={<VideoWatch />} />
            <Route path="/store/ai-studio" element={<AiStudio />} />
            <Route element={<RequireRole roles={["ROOT_SUPER_ADMIN", "SUPER_ADMIN", "PARTNER", "BUSINESS_OWNER", "CUSTOMER"]} />}>
              <Route path="/mkt/account" element={<MyAccount />} />
              <Route path="/freelancer" element={<FreelancerDashboard />} />
              <Route path="/freelancer/listings" element={<FreelancerListings />} />
              <Route path="/freelancer/orders" element={<FreelancerOrders />} />
              <Route path="/freelancer/wallet" element={<FreelancerWallet />} />
              <Route path="/freelancer/affiliate" element={<OwnerAffiliates />} />
              <Route path="/mkt/orders" element={<MyOrders />} />
              <Route path="/mkt/courses" element={<MyCourses />} />
              <Route path="/mkt/messages" element={<Messages />} />
              <Route path="/mkt/messages/:uid" element={<Messages />} />
              <Route path="/mkt/offers" element={<Offers />} />
            </Route>
            <Route path="/creator/login" element={<CreatorLogin />} />
            <Route path="/create" element={<SiteAffiliateLanding />} />
            <Route path="/create-website" element={<SiteAffiliateLegacyRedirect kind="website" />} />
            <Route path="/create-store" element={<SiteAffiliateLegacyRedirect kind="store" />} />
            <Route path="/creator/join" element={<CreatorJoin />} />
            <Route element={<RequireRole roles={["CREATOR"]} />}>
              <Route path="/creator" element={<CreatorDashboard />} />
              <Route path="/creator/videos" element={<CreatorVideos />} />
              <Route path="/creator/videos/new" element={<CreatorVideos autoOpen />} />
              <Route path="/creator/offers" element={<CreatorOffers />} />
              <Route path="/creator/links" element={<CreatorLinks />} />
              <Route path="/creator/earnings" element={<CreatorEarnings />} />
              <Route path="/creator/withdrawals" element={<CreatorEarnings tab="withdrawals" />} />
              <Route path="/creator/monetization" element={<CreatorMonetization />} />
              <Route path="/creator/referrals" element={<CreatorReferrals />} />
              <Route path="/creator/site-affiliate" element={<CreatorSiteAffiliate />} />
              <Route path="/creator/verify" element={<CreatorVerify />} />
              <Route path="/creator/analytics" element={<CreatorAnalytics />} />
              <Route path="/creator/profile" element={<CreatorProfile />} />
              <Route path="/creator/settings" element={<CreatorSettings />} />
              <Route path="/creator/support" element={<CreatorSupport />} />
            </Route>
            <Route element={<RequireRole roles={["ROOT_SUPER_ADMIN"]} />}>
              <Route path="/root" element={<RootDashboard />} />
              <Route path="/root/admins" element={<RootAdmins />} />
              <Route path="/root/access" element={<RootAccess />} />
              <Route path="/root/plans" element={<RootPlans />} />
              <Route path="/root/revenue" element={<RootRevenue />} />
              <Route path="/root/legal" element={<RootLegal />} />
              <Route path="/root/domains" element={<RootDomains />} />
              <Route path="/root/import" element={<RootImport />} />
              <Route path="/root/seo" element={<RootSeo />} />
              <Route path="/root/self-service" element={<RootSelfService />} />
              <Route path="/root/crm" element={<RootCrm />} />
              <Route path="/root/ai" element={<RootAi />} />
              <Route path="/root/challenges" element={<RootChallenges />} />
              <Route path="/root/marketplace" element={<RootMarketplace />} />
              <Route path="/root/creators" element={<RootCreators />} />
              <Route path="/root/ads" element={<RootAds />} />
              <Route path="/root/site-affiliate" element={<RootSiteAffiliate />} />
              <Route path="/root/app-branding" element={<RootAppBranding />} />
              <Route path="/root/app-analytics" element={<RootAppAnalytics />} />
      <Route path="/root/realestate" element={<RootRealEstate />} />
              <Route path="/root/partners" element={<AdminPartners />} />
              <Route path="/root/websites" element={<AdminWebsites />} />
              <Route path="/root/payments" element={<AdminPayments />} />
              <Route path="/root/audit" element={<AdminAudit />} />
            </Route>
            <Route element={<RequireRole roles={["SUPER_ADMIN"]} />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/partners" element={<AdminPartners />} />
              <Route path="/admin/websites" element={<AdminWebsites />} />
              <Route path="/admin/themes" element={<AdminThemes />} />
              <Route path="/admin/payments" element={<AdminPayments />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/audit" element={<AdminAudit />} />
            </Route>
            <Route element={<RequireRole roles={["PARTNER"]} />}>
              <Route path="/partner" element={<PartnerDashboard />} />
              <Route path="/partner/websites" element={<PartnerWebsites />} />
              <Route path="/partner/websites/new" element={<WebsiteWizard />} />
              <Route path="/partner/websites/:wid" element={<WebsiteDetail />} />
              <Route path="/partner/editor/:wid" element={<VisualEditor />} />
              <Route path="/partner/branding" element={<PartnerBranding />} />
              <Route path="/partner/billing" element={<PartnerBilling />} />
              <Route path="/partner/referrals" element={<PartnerReferrals />} />
              <Route path="/partner/leaderboard" element={<PartnerLeaderboard />} />
              <Route path="/partner/challenges" element={<PartnerChallenges />} />
            </Route>
            <Route element={<RequireRole roles={["BUSINESS_OWNER"]} />}>
              <Route path="/store" element={<StoreDashboard />} />
              <Route path="/store/editor" element={<VisualEditor />} />
              <Route path="/store/crm" element={<StoreCrm />} />
              <Route path="/store/ai" element={<GrowthAi />} />
              <Route path="/store/products" element={<StoreProducts />} />
              <Route path="/store/orders" element={<StoreOrders />} />
              <Route path="/store/customers" element={<StoreCustomers />} />
              <Route path="/store/coupons" element={<StoreCoupons />} />
              <Route path="/store/settings" element={<StoreSettings />} />
              <Route path="/store/account" element={<StoreAccount />} />
              <Route path="/store/affiliate" element={<OwnerAffiliates />} />
              <Route path="/store/realestate" element={<ReDashboard />} />
              <Route path="/store/re-org" element={<ReOrgPanel />} />
              <Route path="/store/re-editor" element={<ReEditor />} />
              <Route path="/store/app" element={<StoreApp />} />
              <Route path="/store/categories" element={<StoreCategories />} />
              <Route path="/store/domain" element={<StoreDomain />} />
              <Route path="/store/seo" element={<StoreSeo />} />
            </Route>
            {/* Catch-all must also pass through PlatformOrSite: on custom hosts it enforces
                canonical HTTPS + serves the mapped store; on platform hosts it renders Home. */}
            <Route path="*" element={<PlatformOrSite />} />
          </Routes>
          <PwaByRoute />
          <InstallAppBanner />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
